import React, { useState, useRef, useEffect } from 'react';

const PDF_URL =
	'https://portfolio-projects-2024.s3.us-west-1.amazonaws.com/public/CDM_Whitepaper.pdf';
const PDFJS_CDN =
	'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDFJS_WORKER =
	'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

function usePDFJS() {
	const [ready, setReady] = useState(!!window.pdfjsLib);
	useEffect(() => {
		if (window.pdfjsLib) {
			setReady(true);
			return;
		}
		const script = document.createElement('script');
		script.src = PDFJS_CDN;
		script.onload = () => {
			window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
			setReady(true);
		};
		document.head.appendChild(script);
	}, []);
	return ready;
}

function PDFPage({ pdf, pageNum, scale, onLoad }) {
	const canvasRef = useRef(null);
	const renderTask = useRef(null);

	useEffect(() => {
		if (!pdf || !canvasRef.current) return;
		let cancelled = false;

		pdf.getPage(pageNum).then((page) => {
			if (cancelled) return;
			const viewport = page.getViewport({ scale });
			const canvas = canvasRef.current;
			canvas.width = viewport.width;
			canvas.height = viewport.height;
			const ctx = canvas.getContext('2d');

			if (renderTask.current) renderTask.current.cancel();
			renderTask.current = page.render({ canvasContext: ctx, viewport });
			renderTask.current.promise
				.then(() => {
					if (!cancelled && onLoad) onLoad(viewport);
				})
				.catch(() => {});
		});

		return () => {
			cancelled = true;
			if (renderTask.current) renderTask.current.cancel();
		};
	}, [pdf, pageNum, scale]);

	return (
		<canvas
			ref={canvasRef}
			style={{
				display: 'block',
				maxWidth: '100%',
				boxShadow: '0 8px 48px rgba(0,0,0,0.5)',
				background: '#fff',
			}}
		/>
	);
}

function WhitepaperPage({ C, useBreakpoint }) {
	const { isMobile, px } = useBreakpoint();
	const pdfReady = usePDFJS();
	const [pdf, setPdf] = useState(null);
	const [numPages, setNumPages] = useState(0);
	const [pageNum, setPageNum] = useState(1);
	const [scale, setScale] = useState(isMobile ? 1 : 1.4);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [reading, setReading] = useState(false); // hero → reader transition
	const [pageW, setPageW] = useState(0);
	const readerRef = useRef(null);

	// Load PDF once PDF.js is ready
	useEffect(() => {
		if (!pdfReady) return;
		setLoading(true);
		window.pdfjsLib
			.getDocument(PDF_URL)
			.promise.then((doc) => {
				setPdf(doc);
				setNumPages(doc.numPages);
				setLoading(false);
			})
			.catch((err) => {
				setError(
					'PDF could not be loaded. Make sure VIM_Whitepaper.pdf is in your /public folder.',
				);
				setLoading(false);
			});
	}, [pdfReady]);

	// Adjust scale on resize
	useEffect(() => {
		setScale(1.4);
	}, [isMobile]);

	const goTo = (n) => {
		const clamped = Math.max(1, Math.min(numPages, n));
		setPageNum(clamped);
		readerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const enterReader = () => {
		setReading(true);
		setTimeout(() => readerRef.current?.scrollTo({ top: 0 }), 50);
	};

	// ── HERO ──────────────────────────────────────────────────────────────────
	if (!reading) {
		return (
			<div style={{ background: C.bgDark, minHeight: '100vh' }}>
				<style>{`
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500&family=DM+Mono:wght@400;500&display=swap');
            @keyframes fadeUp { from { opacity:0; transform:translateY(28px); } to { opacity:1; transform:translateY(0); } }
            @keyframes glow   { 0%,100% { opacity:0.4; } 50% { opacity:0.8; } }
            .wp-fade-1 { animation: fadeUp 0.7s 0.1s both; }
            .wp-fade-2 { animation: fadeUp 0.7s 0.25s both; }
            .wp-fade-3 { animation: fadeUp 0.7s 0.4s both; }
            .wp-fade-4 { animation: fadeUp 0.7s 0.55s both; }
            .wp-fade-5 { animation: fadeUp 0.7s 0.7s both; }
            .read-btn {
              display: inline-flex; align-items: center; gap: 10px;
              background: ${C.teal}; color: white;
              font-family: 'DM Mono', monospace; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
              padding: 16px 32px; border: none; cursor: pointer;
              transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
            }
            .read-btn:hover { background: ${C.tealDark}; transform: translateY(-2px); box-shadow: 0 8px 24px ${C.teal}40; }
            .dl-btn {
              display: inline-flex; align-items: center; gap: 10px;
              background: transparent; color: ${C.tealLight};
              font-family: 'DM Mono', monospace; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase;
              padding: 15px 31px; border: 1px solid ${C.teal}60; cursor: pointer;
              transition: all 0.2s; text-decoration: none;
            }
            .dl-btn:hover { border-color: ${C.tealLight}; background: ${C.teal}18; }
          `}</style>

				{/* Background atmospheric glow */}
				<div
					style={{
						position: 'fixed',
						inset: 0,
						pointerEvents: 'none',
						zIndex: 0,
						background: `radial-gradient(ellipse 60% 40% at 50% 60%, ${C.teal}12 0%, transparent 70%)`,
						animation: 'glow 6s ease-in-out infinite',
					}}
				/>

				<div
					style={{
						position: 'relative',
						zIndex: 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '100vh',
						padding: `100px ${px} 80px`,
						textAlign: 'center',
					}}
				>
					{/* Document icon */}
					<div className='wp-fade-1' style={{ marginBottom: 40 }}>
						<svg width='56' height='68' viewBox='0 0 56 68' fill='none'>
							<rect
								x='1'
								y='1'
								width='46'
								height='60'
								rx='2'
								fill='#0E1A18'
								stroke={C.teal}
								strokeWidth='1.5'
							/>
							<path d='M47 1 L55 9 L47 9 Z' fill={C.teal} opacity='0.6' />
							<path
								d='M47 1 L55 9 L47 9 L47 1Z'
								stroke={C.teal}
								strokeWidth='1.5'
							/>
							<line
								x1='10'
								y1='22'
								x2='38'
								y2='22'
								stroke={C.teal}
								strokeWidth='1'
								opacity='0.5'
							/>
							<line
								x1='10'
								y1='30'
								x2='38'
								y2='30'
								stroke={C.teal}
								strokeWidth='1'
								opacity='0.5'
							/>
							<line
								x1='10'
								y1='38'
								x2='28'
								y2='38'
								stroke={C.teal}
								strokeWidth='1'
								opacity='0.5'
							/>
							<circle cx='10' cy='14' r='2' fill={C.tealLight} opacity='0.7' />
							<circle cx='16' cy='14' r='2' fill={C.tealLight} opacity='0.4' />
							<circle cx='22' cy='14' r='2' fill={C.tealLight} opacity='0.2' />
						</svg>
					</div>

					{/* Label */}
					<div
						className='wp-fade-1'
						style={{
							fontFamily: 'DM Mono',
							fontSize: 10,
							letterSpacing: '0.2em',
							textTransform: 'uppercase',
							color: C.tealLight,
							marginBottom: 20,
							display: 'flex',
							alignItems: 'center',
							gap: 12,
						}}
					>
						<span
							style={{
								display: 'inline-block',
								width: 28,
								height: 1,
								background: C.teal,
							}}
						/>
						Academic Whitepaper · 2025
						<span
							style={{
								display: 'inline-block',
								width: 28,
								height: 1,
								background: C.teal,
							}}
						/>
					</div>

					{/* Title */}
					<h1
						className='wp-fade-2'
						style={{
							fontFamily: 'Playfair Display',
							fontWeight: 700,
							fontSize: isMobile
								? 'clamp(28px,7vw,44px)'
								: 'clamp(36px,4vw,60px)',
							color: 'white',
							lineHeight: 1.2,
							maxWidth: 720,
							marginBottom: 8,
						}}
					>
						The Census Disruption Model
					</h1>
					<p
						className='wp-fade-3'
						style={{
							fontFamily: 'Playfair Display',
							fontStyle: 'italic',
							fontSize: isMobile ? 16 : 20,
							color: C.tealLight,
							marginBottom: 40,
						}}
					>
						A Probabilistic Framework for Identifying Disrupted Lives
						<br />
						in Historical Mass Violence
					</p>

					{/* Meta strip */}
					<div
						className='wp-fade-3'
						style={{
							display: 'flex',
							gap: isMobile ? 20 : 48,
							justifyContent: 'center',
							marginBottom: 52,
							flexWrap: 'wrap',
						}}
					>
						{[
							{ label: 'Focus Event', value: '1921 Tulsa Race Massacre' },
							{ label: 'Primary Data', value: 'IPUMS Census Microdata' },
							{ label: 'Pages', value: `${numPages || '—'}` },
						].map(({ label, value }) => (
							<div key={label} style={{ textAlign: 'center' }}>
								<div
									style={{
										fontFamily: 'DM Mono',
										fontSize: 9,
										letterSpacing: '0.15em',
										textTransform: 'uppercase',
										color: '#4A6A66',
										marginBottom: 6,
									}}
								>
									{label}
								</div>
								<div style={{ fontSize: 13, color: '#8AACAA' }}>{value}</div>
							</div>
						))}
					</div>

					{/* Abstract */}
					<div
						className='wp-fade-4'
						style={{
							maxWidth: 580,
							background: '#111E1C',
							border: `1px solid #1E3230`,
							padding: isMobile ? '24px' : '36px',
							marginBottom: 44,
							textAlign: 'left',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 9,
								letterSpacing: '0.18em',
								textTransform: 'uppercase',
								color: C.tealLight,
								marginBottom: 14,
							}}
						>
							Abstract
						</div>
						<p style={{ fontSize: 14, color: '#8AACAA', lineHeight: 1.9 }}>
							Large-scale human tragedies often leave behind incomplete or
							distorted historical records, particularly when victims belong to
							marginalized communities. The Census Disruption Model (CDM)
							combines digitized census microdata, demographic analysis, and
							probabilistic risk modeling to surface individuals whose
							historical continuity was disrupted under conditions consistent
							with mass violence, displacement, or institutional erasure.
						</p>
					</div>

					{/* CTAs */}
					<div
						className='wp-fade-5'
						style={{
							display: 'flex',
							flexDirection: isMobile ? 'column' : 'row',
							gap: 12,
							alignItems: 'center',
						}}
					>
						<button className='read-btn' onClick={enterReader}>
							<span>◈</span> Read the Paper
						</button>
						<a href='/VIM_Whitepaper.docx' className='dl-btn' download>
							↓ Download DOCX
						</a>
					</div>

					{!pdfReady && (
						<div
							style={{
								marginTop: 24,
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#4A6A66',
								letterSpacing: '0.1em',
							}}
						>
							Loading PDF viewer…
						</div>
					)}
				</div>
			</div>
		);
	}

	// ── READING ROOM ─────────────────────────────────────────────────────────────
	return (
		<div
			style={{
				background: '#0A1210',
				minHeight: '100vh',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<style>{`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600&family=DM+Sans:wght@400;500&family=DM+Mono:wght@400;500&display=swap');
          @keyframes fadeIn { from { opacity:0; } to { opacity:1; } }
          .reader-fade { animation: fadeIn 0.5s ease both; }
          .page-btn {
            background: #111E1C; border: 1px solid #1E3230;
            color: #8AACAA; width: 36px; height: 36px;
            display: flex; align-items: center; justify-content: center;
            cursor: pointer; font-size: 16px;
            transition: all 0.15s; flex-shrink: 0;
            font-family: 'DM Mono', monospace;
          }
          .page-btn:hover:not(:disabled) { background: ${C.teal}; border-color: ${C.teal}; color: white; }
          .page-btn:disabled { opacity: 0.25; cursor: default; }
          .zoom-btn {
            background: #111E1C; border: 1px solid #1E3230; color: #8AACAA;
            width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
            cursor: pointer; font-size: 18px; transition: all 0.15s;
            font-family: 'DM Mono', monospace;
          }
          .zoom-btn:hover { background: ${C.teal}18; color: ${C.tealLight}; }
          .page-input {
            background: #0A1210; border: 1px solid #1E3230; color: ${C.tealLight};
            font-family: 'DM Mono', monospace; font-size: 12px; text-align: center;
            width: 44px; padding: 4px;
            outline: none;
          }
          .page-input:focus { border-color: ${C.teal}; }
        `}</style>

			{/* ── TOOLBAR ── */}
			<div
				style={{
					position: 'fixed',
					top: 0,
					left: 0,
					right: 0,
					zIndex: 200,
					background: 'rgba(10,18,16,0.95)',
					backdropFilter: 'blur(12px)',
					borderBottom: '1px solid #1E3230',
					height: 80,
					display: 'flex',
					alignItems: 'center',
					padding: '0 20px',
					gap: 16,
				}}
			>
				{/* Back to hero */}
				<button
					onClick={() => setReading(false)}
					style={{
						background: 'none',
						border: 'none',
						color: '#6A8A86',
						cursor: 'pointer',
						fontFamily: 'DM Mono',
						fontSize: 14,
						letterSpacing: '0.12em',
						textTransform: 'uppercase',
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						padding: 0,
						transition: 'color 0.2s',
						flexShrink: 0,
					}}
					onMouseEnter={(e) => (e.currentTarget.style.color = C.tealLight)}
					onMouseLeave={(e) => (e.currentTarget.style.color = '#6A8A86')}
				>
					← Back
				</button>

				<div style={{ width: 1, height: 24, background: '#1E3230' }} />

				{/* Title */}
				<div
					style={{
						fontFamily: 'DM Mono',
						fontSize: isMobile ? 14 : 14,
						letterSpacing: '0.1em',
						color: '#4A6A66',
						flex: 1,
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						whiteSpace: 'nowrap',
					}}
				>
					{isMobile ? 'CDM Whitepaper' : 'Census Disruption Model · Whitepaper'}
				</div>

				{/* Page navigation */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						flexShrink: 0,
					}}
				>
					<button
						className='page-btn'
						onClick={() => goTo(pageNum - 1)}
						disabled={pageNum <= 1}
					>
						‹
					</button>
					<div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						<input
							className='page-input'
							type='number'
							min={1}
							max={numPages}
							value={pageNum}
							onChange={(e) => goTo(parseInt(e.target.value) || 1)}
						/>
						<span
							style={{ fontFamily: 'DM Mono', fontSize: 14, color: '#4A6A66' }}
						>
							/ {numPages || '—'}
						</span>
					</div>
					<button
						className='page-btn'
						onClick={() => goTo(pageNum + 1)}
						disabled={pageNum >= numPages}
					>
						›
					</button>
				</div>

				<div style={{ width: 1, height: 24, background: '#1E3230' }} />

				{!isMobile && (
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 6,
							flexShrink: 0,
						}}
					>
						<button
							className='zoom-btn'
							onClick={() =>
								setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)))
							}
						>
							−
						</button>
						<span
							style={{
								fontFamily: 'DM Mono',
								fontSize: 14,
								color: '#6A8A86',
								minWidth: 36,
								textAlign: 'center',
							}}
						>
							{Math.round(scale * 100)}%
						</span>
						<button
							className='zoom-btn'
							onClick={() =>
								setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)))
							}
						>
							+
						</button>
					</div>
				)}

				{/* Download */}
				<a
					href='/VIM_Whitepaper.docx'
					download
					style={{
						background: C.teal,
						color: 'white',
						fontFamily: 'DM Mono',
						fontSize: 14,
						letterSpacing: '0.1em',
						textTransform: 'uppercase',
						padding: '8px 16px',
						textDecoration: 'none',
						flexShrink: 0,
						transition: 'background 0.2s',
					}}
					onMouseEnter={(e) => (e.currentTarget.style.background = C.tealDark)}
					onMouseLeave={(e) => (e.currentTarget.style.background = C.teal)}
				>
					{isMobile ? '↓' : '↓ Download'}
				</a>
			</div>

			{/* ── READER BODY ── */}
			<div
				ref={readerRef}
				className='reader-fade'
				style={{
					flex: 1,
					marginTop: 56,
					overflowY: 'auto',
					overflowX: 'auto',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					padding: isMobile ? '32px 12px 60px' : '48px 40px 80px',
					gap: 0,
					// Subtle vignette
					background:
						'radial-gradient(ellipse 100% 60% at 50% 0%, #12201E 0%, #0A1210 50%)',
				}}
			>
				{loading && (
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 20,
							paddingTop: 80,
						}}
					>
						<div
							style={{
								width: 36,
								height: 36,
								border: `2px solid ${C.tealLight}`,
								borderTopColor: 'transparent',
								borderRadius: '50%',
								animation: 'spin 0.8s linear infinite',
							}}
						/>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 11,
								color: '#6A8A86',
								letterSpacing: '0.12em',
							}}
						>
							LOADING DOCUMENT
						</div>
						<style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
					</div>
				)}

				{error && (
					<div
						style={{
							maxWidth: 480,
							marginTop: 60,
							background: '#111E1C',
							border: '1px solid #2A3A38',
							padding: '36px',
							textAlign: 'center',
						}}
					>
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#C4602A',
								letterSpacing: '0.12em',
								textTransform: 'uppercase',
								marginBottom: 14,
							}}
						>
							PDF Not Found
						</div>
						<p
							style={{
								fontSize: 14,
								color: '#8AACAA',
								lineHeight: 1.8,
								marginBottom: 24,
							}}
						>
							{error}
						</p>
						<a
							href='/VIM_Whitepaper.docx'
							download
							style={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 8,
								background: C.teal,
								color: 'white',
								fontFamily: 'DM Mono',
								fontSize: 11,
								letterSpacing: '0.1em',
								textTransform: 'uppercase',
								padding: '12px 24px',
								textDecoration: 'none',
							}}
						>
							↓ Download DOCX Instead
						</a>
					</div>
				)}

				{pdf && !error && (
					<>
						{/* Page counter top */}
						<div
							style={{
								fontFamily: 'DM Mono',
								fontSize: 10,
								color: '#2A4A46',
								letterSpacing: '0.15em',
								textTransform: 'uppercase',
								marginBottom: 20,
							}}
						>
							Page {pageNum} of {numPages}
						</div>

						{/* The PDF page */}
						<div style={{ position: 'relative' }}>
							<PDFPage
								pdf={pdf}
								pageNum={pageNum}
								scale={scale}
								onLoad={(vp) => setPageW(vp.width)}
							/>
						</div>

						{/* Bottom navigation */}
						<div
							style={{
								marginTop: 40,
								display: 'flex',
								alignItems: 'center',
								gap: 16,
							}}
						>
							<button
								onClick={() => goTo(pageNum - 1)}
								disabled={pageNum <= 1}
								style={{
									background: pageNum <= 1 ? 'transparent' : C.teal,
									border: `1px solid ${pageNum <= 1 ? '#1E3230' : C.teal}`,
									color: pageNum <= 1 ? '#2A4A46' : 'white',
									fontFamily: 'DM Mono',
									fontSize: 11,
									letterSpacing: '0.1em',
									textTransform: 'uppercase',
									padding: '12px 24px',
									cursor: pageNum <= 1 ? 'default' : 'pointer',
									transition: 'all 0.2s',
								}}
							>
								← Previous
							</button>

							{/* Page dots (up to 9) */}
							<div style={{ display: 'flex', gap: 6 }}>
								{Array.from({ length: Math.min(numPages, 9) }, (_, i) => {
									const p =
										numPages <= 9
											? i + 1
											: Math.round(1 + (i / 8) * (numPages - 1));
									const active = pageNum === p;
									return (
										<button
											key={i}
											onClick={() => goTo(p)}
											style={{
												width: active ? 24 : 8,
												height: 8,
												borderRadius: 4,
												background: active ? C.teal : '#1E3230',
												border: 'none',
												cursor: 'pointer',
												transition: 'all 0.25s',
											}}
										/>
									);
								})}
							</div>

							<button
								onClick={() => goTo(pageNum + 1)}
								disabled={pageNum >= numPages}
								style={{
									background: pageNum >= numPages ? 'transparent' : C.teal,
									border: `1px solid ${pageNum >= numPages ? '#1E3230' : C.teal}`,
									color: pageNum >= numPages ? '#2A4A46' : 'white',
									fontFamily: 'DM Mono',
									fontSize: 11,
									letterSpacing: '0.1em',
									textTransform: 'uppercase',
									padding: '12px 24px',
									cursor: pageNum >= numPages ? 'default' : 'pointer',
									transition: 'all 0.2s',
								}}
							>
								Next →
							</button>
						</div>

						{/* End state */}
						{pageNum === numPages && (
							<div
								style={{ marginTop: 48, textAlign: 'center', maxWidth: 400 }}
							>
								<div
									style={{
										fontFamily: 'DM Mono',
										fontSize: 10,
										color: C.tealLight,
										letterSpacing: '0.15em',
										textTransform: 'uppercase',
										marginBottom: 16,
									}}
								>
									End of Document
								</div>
								<p
									style={{
										fontFamily: 'Playfair Display',
										fontStyle: 'italic',
										fontSize: 18,
										color: '#4A6A66',
										lineHeight: 1.6,
										marginBottom: 28,
									}}
								>
									"This work does not seek to close the record, but to reopen it
									responsibly."
								</p>
								<div
									style={{
										display: 'flex',
										gap: 12,
										justifyContent: 'center',
										flexWrap: 'wrap',
									}}
								>
									<button
										onClick={() => goTo(1)}
										style={{
											background: 'transparent',
											border: `1px solid #1E3230`,
											color: '#6A8A86',
											fontFamily: 'DM Mono',
											fontSize: 10,
											letterSpacing: '0.1em',
											textTransform: 'uppercase',
											padding: '10px 20px',
											cursor: 'pointer',
											transition: 'all 0.2s',
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.borderColor = C.teal;
											e.currentTarget.style.color = C.teal;
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.borderColor = '#1E3230';
											e.currentTarget.style.color = '#6A8A86';
										}}
									>
										↑ Back to Start
									</button>
									<a
										href='/VIM_Whitepaper.docx'
										download
										style={{
											background: C.teal,
											color: 'white',
											fontFamily: 'DM Mono',
											fontSize: 10,
											letterSpacing: '0.1em',
											textTransform: 'uppercase',
											padding: '10px 20px',
											textDecoration: 'none',
										}}
									>
										↓ Download DOCX
									</a>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

export default WhitepaperPage;
